import {
  type BetaFeedbackCreateInput,
  BetaFeedbackCreateInputSchema,
  type BetaFeedbackCreateResponse,
  BetaFeedbackCreateResponseSchema,
  type BetaFeedbackListResponse,
  BetaFeedbackListResponseSchema,
  type BetaFeedbackUpdateInput,
  BetaFeedbackUpdateInputSchema,
  type BetaFeedbackUpdateResponse,
  BetaFeedbackUpdateResponseSchema,
} from '@sazeo/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

export const FEEDBACK_SCREENSHOT_BUCKET = 'feedback-screenshots';
export const FEEDBACK_SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;
export const FEEDBACK_SCREENSHOT_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export type FeedbackScreenshotMimeType = (typeof FEEDBACK_SCREENSHOT_MIME_TYPES)[number];

type StorageUploadResult = {
  data: { path: string } | null;
  error: { message: string } | null;
};

export interface FeedbackStorageClientLike extends SupabaseClientLike {
  storage: {
    from: (bucket: string) => {
      upload: (
        path: string,
        file: File,
        options: { contentType: string; upsert: boolean },
      ) => Promise<StorageUploadResult>;
    };
  };
}

export class BetaFeedbackApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'BetaFeedbackApiError';
    this.code = code;
    this.status = status;
  }
}

type ErrorEnvelope = {
  error?: {
    code?: string;
    message?: string;
  };
};

export const betaFeedbackKeys = {
  all: ['beta-feedback'] as const,
  list: (tenantId?: string) => [...betaFeedbackKeys.all, 'list', tenantId ?? 'active'] as const,
} as const;

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function readError(res: Response): Promise<BetaFeedbackApiError> {
  const body = (await res.json().catch(() => null)) as ErrorEnvelope | null;
  return new BetaFeedbackApiError(
    body?.error?.code ?? 'feedback_failed',
    body?.error?.message ?? res.statusText,
    res.status,
  );
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  parse: (body: unknown) => T,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });

  if (!res.ok) {
    throw await readError(res);
  }

  return parse(await res.json());
}

export function listBetaFeedback(supabase: SupabaseClientLike): Promise<BetaFeedbackListResponse> {
  return apiFetch(supabase, 'beta-feedback', (body) => BetaFeedbackListResponseSchema.parse(body));
}

export function submitBetaFeedback(
  supabase: SupabaseClientLike,
  input: BetaFeedbackCreateInput,
): Promise<BetaFeedbackCreateResponse> {
  const payload = BetaFeedbackCreateInputSchema.parse(input);
  return apiFetch(
    supabase,
    'beta-feedback',
    (body) => BetaFeedbackCreateResponseSchema.parse(body),
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export function updateBetaFeedbackStatus(
  supabase: SupabaseClientLike,
  input: BetaFeedbackUpdateInput,
): Promise<BetaFeedbackUpdateResponse> {
  const payload = BetaFeedbackUpdateInputSchema.parse(input);
  return apiFetch(
    supabase,
    `beta-feedback/${payload.id}`,
    (body) => BetaFeedbackUpdateResponseSchema.parse(body),
    {
      method: 'PATCH',
      body: JSON.stringify({ status: payload.status }),
    },
  );
}

function extensionForMimeType(type: string): 'png' | 'jpg' | 'webp' {
  if (type === 'image/png') return 'png';
  if (type === 'image/webp') return 'webp';
  return 'jpg';
}

export function isFeedbackScreenshotMimeType(type: string): type is FeedbackScreenshotMimeType {
  return (FEEDBACK_SCREENSHOT_MIME_TYPES as readonly string[]).includes(type);
}

export async function uploadBetaFeedbackScreenshot(
  supabase: FeedbackStorageClientLike,
  input: {
    tenantId: string;
    userId: string;
    file: File;
  },
): Promise<string> {
  if (!isFeedbackScreenshotMimeType(input.file.type)) {
    throw new BetaFeedbackApiError('invalid_screenshot_type', 'Unsupported screenshot type', 400);
  }
  if (input.file.size > FEEDBACK_SCREENSHOT_MAX_BYTES) {
    throw new BetaFeedbackApiError('screenshot_too_large', 'Screenshot is too large', 400);
  }

  const path = `${input.tenantId}/${input.userId}/${crypto.randomUUID()}.${extensionForMimeType(input.file.type)}`;
  const { data, error } = await supabase.storage
    .from(FEEDBACK_SCREENSHOT_BUCKET)
    .upload(path, input.file, {
      contentType: input.file.type,
      upsert: false,
    });

  if (error) {
    throw new BetaFeedbackApiError('screenshot_upload_failed', error.message, 500);
  }

  return data?.path ?? path;
}

export function useBetaFeedback(supabase: SupabaseClientLike, tenantId?: string) {
  return useQuery({
    queryKey: betaFeedbackKeys.list(tenantId),
    queryFn: () => listBetaFeedback(supabase),
    enabled: tenantId === undefined || Boolean(tenantId),
  });
}

export function useSubmitBetaFeedback(supabase: SupabaseClientLike, tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BetaFeedbackCreateInput) => submitBetaFeedback(supabase, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: betaFeedbackKeys.list(tenantId) });
    },
  });
}

export function useUpdateBetaFeedbackStatus(supabase: SupabaseClientLike, tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BetaFeedbackUpdateInput) => updateBetaFeedbackStatus(supabase, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: betaFeedbackKeys.list(tenantId) });
    },
  });
}
